import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import CarWithDriverAddCarForm, { CarEntry } from './CarWithDriverAddCarForm'
import { BottomNav, ProgressHeader, TopBar } from './CarWithDriverShared'
import { useCwdLang } from './useCwdLang'

interface CarWithDriverStep2Props {
  cars: CarEntry[]
  onCarsChange: (cars: CarEntry[]) => void
  currentStep: number
  totalSteps: number
  maxStep?: number
  onExit: () => void
  onBack: () => void
  onNext: (data: any) => Promise<void>
}

type View = 'list' | 'add' | 'edit'

const CarWithDriverStep2: React.FC<CarWithDriverStep2Props> = ({
  cars,
  onCarsChange,
  currentStep,
  totalSteps,
  maxStep,
  onExit,
  onBack,
  onNext,
}) => {
  const { t } = useCwdLang()
  const [view, setView] = useState<View>('list')
  const [editingEntry, setEditingEntry] = useState<CarEntry | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string>('')

  const handleAddConfirm = (entry: CarEntry) => {
    if (editingEntry) {
      onCarsChange(cars.map((c) => (c.id === entry.id ? entry : c)))
    } else {
      onCarsChange([...cars, entry])
    }
    setEditingEntry(undefined)
    setView('list')
  }

  const handleDelete = (id: string) => {
    onCarsChange(cars.filter((c) => c.id !== id))
  }

  const handleNext = async () => {
    if (cars.length === 0) {
      setErrors(t.step2.errNoCar)
      return
    }
    setErrors('')
    setIsSubmitting(true)
    await onNext({ cars })
    setIsSubmitting(false)
  }

  if (view === 'add' || view === 'edit') {
    return (
      <CarWithDriverAddCarForm
        entry={editingEntry}
        onConfirm={handleAddConfirm}
        onBack={() => {
          setEditingEntry(undefined)
          setView('list')
        }}
        onExit={onExit}
      />
    )
  }

  return (
    <div
      style={{
        fontFamily: 'Poppins, sans-serif',
        backgroundColor: '#fff',
        minHeight: '100vh',
        maxWidth: '500px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <TopBar onExit={onExit} />
      <ProgressHeader
        currentStep={currentStep}
        totalSteps={totalSteps}
        maxStep={maxStep}
        title={t.step2.title}
        currentStepProgress={cars.length > 0 ? 100 : 0}
      />

      {/* Car list */}
      <div style={{ flex: 1, padding: '0 16px', overflowY: 'auto' }}>
        {cars.map((car) => (
          <div
            key={car.id}
            style={{
              border: '1px solid #D6DAE0',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '15px', color: '#212427' }}
              >
                {car.quantity}x {car.carType}
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '12px',
                  color: '#667085',
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <span>
                  <img
                    src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1zjzpxvw6prTsEgTxSLJLqwXKGp3W3eNtYibTcRtPAo3gM6zcTTTLZConyV8eQsTEMfJbyJu8N9mQpCFpKxua2W4.png"
                    alt="car"
                    style={{
                      width: '14px',
                      height: '14px',
                      objectFit: 'contain',
                      verticalAlign: 'middle',
                      marginRight: '3px',
                    }}
                  />
                  {car.carBrand.length > 8 ? car.carBrand.slice(0, 8) + '…' : car.carBrand}
                </span>
                <span>
                  <img
                    src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/28x5B5bSXHbikaCs9E6CuFwF6o35vHwdrCdQqCv3vUDTzRXj617fqQNsZHehAF76jjpBkQD2xZ7QkRZcikSxorMo.png"
                    alt="seat"
                    style={{
                      width: '14px',
                      height: '14px',
                      objectFit: 'contain',
                      verticalAlign: 'middle',
                      marginRight: '3px',
                    }}
                  />
                  {t.step2.maxPassengers(car.maxPassengers)}
                </span>
                <span>
                  <img
                    src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/5w2pLLxGbrrX17WSGmcwQFYoLGYuvskED5w3jebk4W6e5KHNPQ9PnuJeyeQBHy4u5LyLexMtaJ7EqmQmz5AQryBA.png"
                    alt="luggage"
                    style={{
                      width: '14px',
                      height: '14px',
                      objectFit: 'contain',
                      verticalAlign: 'middle',
                      marginRight: '3px',
                    }}
                  />
                  {t.step2.largeLuggage(car.maxStandardLuggages)}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '8px' }}>
              <button
                onClick={() => {
                  setEditingEntry(car)
                  setView('edit')
                }}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: '1px solid #0048B3',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  color: '#0048B3',
                }}
              >
                <EditOutlined />
              </button>
              <button
                onClick={() => handleDelete(car.id)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: '1px solid #ef4444',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  color: '#ef4444',
                }}
              >
                <DeleteOutlined />
              </button>
            </div>
          </div>
        ))}

        {errors && (
          <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 12px' }}>{errors}</p>
        )}

        {/* Add car type button */}
        <button
          onClick={() => {
            setEditingEntry(undefined)
            setView('add')
          }}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#EFF6FF',
            color: '#0048B3',
            border: '1px dashed #0048B3',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '15px',
            fontFamily: 'Poppins, sans-serif',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '20px',
          }}
        >
          {t.step2.addCarType}
        </button>
      </div>

      <BottomNav
        onBack={onBack}
        onNext={handleNext}
        nextDisabled={cars.length === 0}
        loading={isSubmitting}
      />
    </div>
  )
}

export default CarWithDriverStep2
